import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { MdSearch, MdLocationOn } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import { createManufacturerVenue, fetchManufacturerVenues } from '../../../../services/Http/ManufacturerApi';
import ManufacturerVenueCreateModal from './ManufacturerVenueCreateModal';
import { useCurrency } from '../../../../context/CurrencyProvider';

const ManufacturerVenues = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const navigate = useNavigate();
    const [records, setRecords] = useState([]);
    const [meta, setMeta] = useState({ total: 0, per_page: 10, current_page: 1 });
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [creating, setCreating] = useState(false);

    const loadVenues = useCallback(async ({ page = 1, perPage = 10, searchTerm = '' } = {}) => {
        setLoading(true);
        try {
            const response = await fetchManufacturerVenues({ page, perPage, search: searchTerm });
            setRecords(response.data || []);
            setMeta(response.meta || { total: 0, per_page: perPage, current_page: page });
        } catch (error) {
            toast.error(t('ManufacturerVenues.errors.loadList'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadVenues();
    }, [loadVenues]);

    const handleSearch = (event) => {
        event.preventDefault();
        loadVenues({ page: 1, perPage: meta.per_page, searchTerm: search });
    };

    const handleCreate = async (payload) => {
        try {
            setCreating(true);
            await createManufacturerVenue(payload);
            toast.success(t('ManufacturerVenues.create.success'));
            setShowModal(false);
            loadVenues();
        } catch (error) {
            toast.error(t('ManufacturerVenues.create.error'));
        } finally {
            setCreating(false);
        }
    };

    const columns = [
        {
            name: t('ManufacturerVenues.table.columns.venue'),
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: t('ManufacturerVenues.table.columns.location'),
            selector: (row) => `${row.city || ''}${row.country ? `, ${row.country}` : ''}`,
            sortable: true,
        },
        {
            name: t('ManufacturerVenues.table.columns.sellThroughValue'),
            selector: (row) => formatResolvedAmount({
                eurAmount: row.sell_through_value,
                localAmount: row.sell_through_value_local ?? row.sell_through_local,
                localCurrencyId: row.sell_through_currency_id ?? row.currency_id,
                localCurrencyCode: row.sell_through_currency_code ?? row.currency_code,
            }),
            sortable: true,
        },
        {
            name: t('ManufacturerVenues.table.columns.lastActivity'),
            selector: (row) => formatDate(row.last_transaction_at),
            sortable: true,
        },
        {
            name: t('ManufacturerVenues.table.columns.action'),
            cell: (row) => (
                <ActionButton onClick={() => navigate(`/venues/${row.id}`)}>
                    {t('ManufacturerVenues.table.view')}
                </ActionButton>
            ),
        },
    ];

    return (
        <PageContainer>
            <PageHeader>
                <TitleBlock>
                    <PageTitle>{t('ManufacturerVenues.title')}</PageTitle>
                    <Subtitle>{t('ManufacturerVenues.subtitle')}</Subtitle>
                </TitleBlock>
                <IconBadge>
                    <MdLocationOn size={28} />
                </IconBadge>
            </PageHeader>
            <PageContent>
                <Toolbar>
                    <SearchForm onSubmit={handleSearch}>
                        <MdSearch />
                        <input
                            type="text"
                            placeholder={t('ManufacturerVenues.table.search')}
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </SearchForm>
                    <PrimaryButton type="button" onClick={() => setShowModal(true)}>
                        {t('ManufacturerVenues.create.button')}
                    </PrimaryButton>
                </Toolbar>
                <DataTable
                    columns={columns}
                    data={records}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={meta.total}
                    paginationPerPage={meta.per_page}
                    onChangePage={(page) => loadVenues({ page, perPage: meta.per_page, searchTerm: search })}
                    onChangeRowsPerPage={(perPage, page) => loadVenues({ page, perPage, searchTerm: search })}
                    highlightOnHover
                    persistTableHead
                />
            </PageContent>
            <ManufacturerVenueCreateModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSubmit={handleCreate}
                submitting={creating}
            />
        </PageContainer>
    );
};

export default ManufacturerVenues;

const TitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const IconBadge = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: rgba(130, 22, 146, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(130, 22, 146, 1);
`;

const Toolbar = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
    gap: 1rem;
    flex-wrap: wrap;
`;

const SearchForm = styled.form`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(130, 22, 146, 0.05);
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 8px;
    padding: 0.375rem 0.75rem;

    input {
        border: none;
        background: transparent;
        outline: none;
        width: 220px;
    }

    svg {
        color: rgba(130, 22, 146, 1);
    }
`;

const PrimaryButton = styled.button`
    padding: 0.625rem 1.125rem;
    background: rgba(130, 22, 146, 1);
    color: #fff;
    border: none;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
        background: rgba(130, 22, 146, 0.9);
    }
`;

const ActionButton = styled.button`
    padding: 0.375rem 0.75rem;
    border-radius: 6px;
    border: 1px solid rgba(130, 22, 146, 0.4);
    background: transparent;
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: rgba(130, 22, 146, 0.08);
    }
`;

const formatDate = (value) => {
    if (!value) {
        return '—';
    }
    return new Date(value).toLocaleDateString();
};
